<template>
  <div>
    <!-- Statistics Cards -->
    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 mb-6"
    >
      <!-- Facilities Created -->
      <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div class="flex items-center mb-4">
          <p class="text-base font-semibold text-gray-900">
            <Translation
              name="dashboard.facilities_created"
              default="Facilities Created"
            />
          </p>
          <button
            ref="facilitiesCreatedTooltipRef"
            class="ml-2 text-gray-400 hover:text-gray-600"
          >
            <InfoIconSmall />
          </button>
        </div>
        <div class="flex justify-between items-center">
          <p class="text-4xl font-semibold text-gray-900 mb-1">
            {{ dashboardData.facilities_count || 0 }}
          </p>
          <div class="flex-shrink-0">
            <BuildingCircleIcon />
          </div>
        </div>
      </div>

      <!-- Calculations in Draft -->
      <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div class="flex items-center mb-4">
          <p class="text-base font-semibold text-gray-900">
            <Translation
              name="dashboard.calculations_in_draft"
              default="Calculations in Draft"
            />
          </p>
          <button
            ref="calculationsInDraftTooltipRef"
            class="ml-2 text-gray-400 hover:text-gray-600"
          >
            <InfoIconSmall />
          </button>
        </div>
        <div class="flex justify-between items-center">
          <p class="text-4xl font-semibold text-gray-900 mb-1">
            {{ dashboardData.calculations_in_draft || 0 }}
          </p>
          <div class="flex-shrink-0">
            <EditCircleIcon />
          </div>
        </div>
      </div>

      <!-- Completed Reports -->
      <div
        class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm sm:col-span-2 sm:mx-auto md:col-span-1 sm:w-1/2 md:w-full"
      >
        <div class="flex items-center mb-4">
          <p class="text-base font-semibold text-gray-900">
            <Translation
              name="dashboard.completed_reports"
              default="Completed Reports"
            />
          </p>
          <button
            ref="completedReportsTooltipRef"
            class="ml-2 text-gray-400 hover:text-gray-600"
          >
            <InfoIconSmall />
          </button>
        </div>
        <div class="flex justify-between items-center">
          <p class="text-4xl font-semibold text-gray-900 mb-1">
            {{ dashboardData.completed_reports || 0 }}
          </p>
          <div class="flex-shrink-0">
            <CheckCircleIcon />
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Edited Facilities -->
    <div class="mb-6">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">
          <Translation
            name="dashboard.recent_edited_facilities"
            default="Recent Edited Facilities"
          />
        </h2>
        <a
          :href="`/${locale}/facilities`"
          class="text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          <Translation name="dashboard.see_all" default="See All" />
        </a>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <a
          v-for="facility in recentFacilities"
          :key="facility.id"
          :href="`/${locale}/facilities/${facility.id}`"
          class="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all flex flex-col"
        >
          <div
            class="rounded-t-xl bg-cover bg-center h-16"
            :style="{
              backgroundImage: `url('/images/facility-card-header.png')`,
            }"
          ></div>
          <div class="px-6 pb-6 pt-2 flex flex-col flex-1">
            <!-- Country Flag and Name -->
            <div class="flex items-center mb-4">
              <div v-if="facility.country" class="mr-2 flex-shrink-0">
                <span
                  :class="`fi fi-${facility.country.toLowerCase()}`"
                  class="rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm"
                  style="
                    font-size: 32px;
                    width: 32px;
                    height: 32px;
                    min-width: 24px;
                    min-height: 24px;
                  "
                ></span>
              </div>
              <span class="text-base font-semibold text-gray-900">
                {{ facility.country_name }}
              </span>
            </div>

            <!-- Facility Name -->
            <h3 class="text-xl font-bold text-gray-900 mb-4">
              {{ facility.name }}
            </h3>

            <div class="mt-auto pt-4 border-t border-gray-200 grid grid-cols-2">
              <div>
                <div class="text-sm text-gray-500 mb-1">
                  <Translation
                    name="dashboard.facility_id"
                    default="Facility ID"
                  />
                </div>
                <div
                  class="text-sm font-semibold text-gray-500 px-1 bg-gray-100 rounded-sm w-fit"
                >
                  {{ facility.facility_id }}
                </div>
              </div>
              <div>
                <div class="text-sm text-gray-500 mb-1">
                  <Translation name="dashboard.linked" default="Linked" />
                </div>
                <div
                  class="flex items-center gap-0.5 text-sm font-semibold text-gray-500 px-2 bg-gray-100 rounded-sm w-fit"
                >
                  <span class="mr-0.5">
                    {{ facility.linked_calculations_count }}</span
                  >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.3333 6.33325V8.59992C11.3333 8.97329 11.3333 9.15997 11.2607 9.30258C11.1968 9.42802 11.0948 9.53001 10.9693 9.59392C10.8267 9.66659 10.64 9.66659 10.2667 9.66659L2 9.66658M8.66667 9.66659V11.9333C8.66667 12.3066 8.66667 12.4933 8.594 12.6359C8.53009 12.7614 8.4281 12.8633 8.30266 12.9273C8.16005 12.9999 7.97337 12.9999 7.6 12.9999H2M2 1.33325L2 14.6666M2 6.33325L12.9333 6.33325C13.3067 6.33325 13.4934 6.33325 13.636 6.26059C13.7614 6.19667 13.8634 6.09469 13.9273 5.96925C14 5.82664 14 5.63995 14 5.26659V4.06659C14 3.69322 14 3.50653 13.9273 3.36393C13.8634 3.23848 13.7614 3.1365 13.636 3.07258C13.4934 2.99992 13.3067 2.99992 12.9333 2.99992L2 2.99992L2 6.33325Z"
                      stroke="#697586"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- Living Wage Calculations -->
    <div class="pb-2 flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-900">
        <Translation
          name="dashboard.living_wage_calculations.title"
          default="Living Wage Calculations"
        />
      </h3>
      <a
        :href="`/${locale}/wage-calculations`"
        class="text-sm font-medium text-brand-600 hover:text-brand-700"
      >
        <Translation name="dashboard.see_all" default="See All" />
      </a>
    </div>
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <WageCalculationsList
        :calculations="livingWageCalculations"
        :available-years="availableYears"
        :locale="locale"
        :search-param="initialSearch"
        :year-param="initialYear"
        :status-param="initialStatus"
        :archive-url-template="archiveUrlTemplate"
        :report-url-template="reportUrlTemplate"
        :wizard-continue-url-template="wizardContinueUrlTemplate"
        :wizard-first-step-url-template="wizardFirstStepUrlTemplate"
        :reopen-url-template="reopenUrlTemplate"
        :copy-url-template="copyUrlTemplate"
        :show-title="false"
        list-path="dashboard"
        :enable-url-sync="true"
      />
    </div>

    <div class="pb-2 flex items-center justify-between mt-12">
      <div class="flex flex-col gap-y-0.5">
        <h3 class="text-lg font-semibold text-gray-900">
          <Translation
            name="dashboard.living_wage_calculations.legacy"
            default="Legacy Living Wage Gap Calculations"
          />
        </h3>
        <p class="text-gray-600">
          <Translation
            name="dashboard.living_wage_calculations.legacy_subtitle"
            default="Some subtitle here"
          />
        </p>
      </div>
    </div>
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <WageCalculationsList
        :legacy="true"
        :calculations="legacyCalculations"
        :available-years="availableYears"
        :locale="locale"
        :report-url-template="legacyReportUrlTemplate"
        :show-title="false"
        :enable-url-sync="false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import Translation from "./aft/Translation.vue";
import BuildingCircleIcon from "./icons/BuildingCircleIcon.vue";
import EditCircleIcon from "./icons/EditCircleIcon.vue";
import CheckCircleIcon from "./icons/CheckCircleIcon.vue";
import InfoIconSmall from "./icons/InfoIconSmall.vue";
import WageCalculationsList from "./WageCalculationsList.vue";

// Props
const props = defineProps({
  dashboardData: {
    type: Object,
    default: () => ({}),
  },
  locale: {
    type: String,
    required: true,
  },
  initialSearch: {
    type: String,
    default: "",
  },
  initialYear: {
    type: String,
    default: "all",
  },
  initialStatus: {
    type: String,
    default: "all",
  },
  reportUrlTemplate: {
    type: String,
    required: true,
  },
  legacyReportUrlTemplate: {
    type: String,
    required: false,
  },
  wizardContinueUrlTemplate: {
    type: String,
    required: true,
  },
  wizardFirstStepUrlTemplate: {
    type: String,
    required: true,
  },
  archiveUrlTemplate: {
    type: String,
    required: true,
  },
  reopenUrlTemplate: {
    type: String,
    required: true,
  },
  copyUrlTemplate: {
    type: String,
    required: true,
  },
});

// Tooltip refs
const facilitiesCreatedTooltipRef = ref(null);
const calculationsInDraftTooltipRef = ref(null);
const completedReportsTooltipRef = ref(null);
const inAuditTooltipRef = ref(null);

// Get tooltip content from database (passed via props)
const getTooltipContent = (key) => {
  return props.dashboardData.tooltips?.[key] || "";
};

// Initialize tooltips
onMounted(() => {
  const tooltipOptions = {
    allowHTML: true,
    theme: "light-border",
    placement: "top",
    arrow: true,
    maxWidth: 300,
  };

  const tooltipConfigs = [
    { ref: facilitiesCreatedTooltipRef, key: "dashboard.facilities_created" },
    {
      ref: calculationsInDraftTooltipRef,
      key: "dashboard.calculations_in_draft",
    },
    { ref: completedReportsTooltipRef, key: "dashboard.completed_reports" },
    { ref: inAuditTooltipRef, key: "dashboard.in_audit" },
  ];

  tooltipConfigs.forEach(({ ref, key }) => {
    const content = getTooltipContent(key);
    if (ref.value && content) {
      tippy(ref.value, {
        ...tooltipOptions,
        content,
      });
    }
  });
});

// Dashboard data as ref for reactivity
const dashboardData = ref(props.dashboardData);

// Computed properties
const livingWageCalculations = computed(() => {
  return dashboardData.value.living_wage_calculations || [];
});

const legacyCalculations = computed(() => {
  return dashboardData.value.legacy_calculations || [];
});

const availableYears = computed(() => {
  return dashboardData.value.available_years || [];
});

const recentFacilities = computed(() => {
  return dashboardData.value.facilities || [];
});
</script>
